/**
 * @fileoverview Service layer for managing conversation history.
 * Handles fetching conversations and messages from the backend API.
 *
 * @module services/conversationService
 */

import { supabase } from './supabase'

/**
 * Service for managing conversation history and messages.
 */
export const conversationService = {
  /**
   * Fetches the user's conversation list.
   *
   * @async
   * @returns {Promise<Object>} Response with conversations array
   * @throws {Error} If the request fails or user is not authenticated
   */
  async fetchUserConversations() {
    try {
      // Get the current session
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession()

      if (sessionError) {
        throw new Error(`Failed to get session: ${sessionError.message}`)
      }

      if (!session) {
        return { conversations: [], error: 'Not authenticated' }
      }

      // Fetch conversations from API
      const response = await fetch('/api/conversations', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to fetch conversations')
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error fetching conversations:', error)
      return { conversations: [], error: error.message }
    }
  },

  /**
   * Fetches messages for a specific conversation.
   *
   * @async
   * @param {string} conversationId - The conversation UUID
   * @returns {Promise<Object>} Response with messages array
   * @throws {Error} If the request fails or user is not authenticated
   */
  async fetchConversationMessages(conversationId) {
    try {
      // Validate conversation ID
      if (!conversationId || conversationId.length !== 36) {
        throw new Error('Invalid conversation ID')
      }

      // Get the current session
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession()

      if (sessionError) {
        throw new Error(`Failed to get session: ${sessionError.message}`)
      }

      if (!session) {
        return { messages: [], error: 'Not authenticated' }
      }

      // Fetch messages from API
      const response = await fetch(`/api/conversations/${conversationId}/messages`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to fetch messages')
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error fetching messages:', error)
      return { messages: [], error: error.message }
    }
  },

  /**
   * Formats messages for display in the chat interface.
   * Handles the metadata structure where content is stored in metadata.message
   * and type information for proper UI rendering.
   * Now also handles new sentence-based structure for assistant messages.
   * Converts database format (content.messages with sentenceUUID) to localStorage format (content.parts with sentence_id).
   *
   * @param {Array} messages - Raw messages from the API
   * @returns {Array} Formatted messages for chat display
   */
  formatMessagesForChat(messages) {
    if (!Array.isArray(messages)) {
      return []
    }

    return messages.map((msg) => {
      let content = msg.content || ''
      let type = msg.type || 'response'
      let sentences = null

      // Handle new sentence-based structure for assistant messages
      if (msg.role === 'assistant' && msg.content && typeof msg.content === 'object') {
        // Check if database returns content.messages array (needs field mapping)
        if (msg.content.messages && Array.isArray(msg.content.messages)) {
          // Convert database format to localStorage parts format
          const parts = msg.content.messages.map((msgObj, index) => {
            const part = {
              type: 'response',
              message: msgObj.message,
              sentence_id: msgObj.sentenceUUID, // Map sentenceUUID to sentence_id
              isComponent: msgObj.isComponent || false,
              componentType: msgObj.componentType,
              isTLDR: msgObj.isTLDR || false,
              order: msgObj.order !== undefined ? msgObj.order : index, // Preserve order or use index
              timestamp: msgObj.timestamp || new Date().toISOString(),
            }

            // Extract component data for heading components
            if (part.isComponent && part.componentType === 'heading') {
              // Parse heading marker format: [[heading:Research Response:1]]
              const headingMatch = part.message.match(/^\[\[heading:(.+?):(\d+)\]\]$/)
              if (headingMatch) {
                const [, title, level] = headingMatch
                part.componentData = {
                  title,
                  level: parseInt(level, 10),
                  displayText: title,
                }
                // Keep the raw marker in message field for compatibility
                part.title = title
                part.level = parseInt(level, 10)
                part.displayText = title
              }
            }

            return part
          })

          // Create localStorage-compatible structure
          msg.content = {
            type: msg.content.type || 'response',
            parts,
          }

          // Set type from the messages structure
          type = msg.content.type || 'response'
        }

        // Check if it's the new parts structure (from database or localStorage)
        if (msg.content.type === 'response' && Array.isArray(msg.content.parts)) {
          // Extract content from parts array for display
          content = msg.content.parts.map((part) => part.message).join('')
          type = msg.content.type || 'response'
        }
        // Check if it's the legacy sentence structure
        else if (msg.content.type === 'response' && Array.isArray(msg.content.sentences)) {
          // Extract sentences array and join text for backward compatibility
          sentences = msg.content.sentences
          content = sentences.map((sentence) => sentence.text).join('')
          type = msg.content.type || 'response'
        }
        // Legacy object structure with message field
        else if (msg.content.message !== undefined) {
          content = msg.content.message
          type = msg.content.type || type
        }
      }
      // Also check metadata for backward compatibility
      else if (msg.metadata?.message !== undefined) {
        content = msg.metadata.message
        type = msg.metadata.type || type
      }
      // Use content as-is if it's a string
      else {
        content = msg.content || ''
      }

      const formattedMessage = {
        role: msg.role,
        content,
        type,
        // Set UI flags for special message types
        isError: msg.isError || type === 'error',
        isProgress: msg.isProgress || type === 'progress' || type === 'status',
        // Include trace for error messages (for admin users)
        trace: msg.trace || msg.metadata?.trace,
        message_id: msg.id || msg.message_id,
        conversation_id: msg.conversation_id,
        messageId: msg.id || msg.message_id, // Keep legacy field for backward compatibility
        timestamp: msg.timestamp || msg.created_at,
        metadata: msg.metadata || {},
      }

      // Add parts data for interactive components (preferred format)
      if (msg.content && typeof msg.content === 'object' && Array.isArray(msg.content.parts)) {
        // Set parts directly on the message object, not nested in content
        formattedMessage.parts = msg.content.parts
        formattedMessage.hasClickableSentences = true
        // Extract text content from parts for the content field
        formattedMessage.content = msg.content.parts
          .filter((part) => part.type === 'response' && !part.isComponent)
          .map((part) => part.message || '')
          .join('')
      }
      // Add sentences data for backward compatibility (legacy format)
      else if (sentences) {
        // Map uuid to sentence_id for consistency with ChatMessageWithParts.jsx
        const sentencesWithIds = sentences.map((sentence) => ({
          ...sentence,
          sentence_id: sentence.uuid, // Critical: renderer expects sentence_id
        }))

        formattedMessage.sentences = sentencesWithIds
        formattedMessage.hasClickableSentences = true
      }

      return formattedMessage
    })
  },

  /**
   * Gets the authentication token for API requests.
   *
   * @async
   * @returns {Promise<string|null>} The JWT token or null if not authenticated
   */
  async getAuthToken() {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      return session?.access_token || null
    } catch (error) {
      console.error('Error getting auth token:', error)
      return null
    }
  },

  /**
   * Clears all conversation data from localStorage.
   * Used when deleting the active conversation.
   */
  clearAllConversationData() {
    try {
      // Clear conversation history from localStorage
      localStorage.removeItem('conversationHistory')
      localStorage.removeItem('resumedConversation')
      // Clear conversation-related localStorage items (UUID keys)
      localStorage.removeItem('et_ai_conversation_id')
      localStorage.removeItem('et_ai_conversation_start_time')
      return true
    } catch (error) {
      console.error('Error clearing conversation data:', error)
      return false
    }
  },

  /**
   * Formats conversation history for display.
   *
   * @param {Array} conversations - Raw conversations from the API
   * @returns {Array} Formatted conversations for display
   */
  formatConversationsForDisplay(conversations) {
    if (!Array.isArray(conversations)) {
      return []
    }

    return conversations.map((conv) => {
      const date = new Date(conv.lastMessageAt || conv.updated_at)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      let timeAgo
      if (diffMins < 1) {
        timeAgo = 'Just now'
      } else if (diffMins < 60) {
        timeAgo = `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
      } else if (diffHours < 24) {
        timeAgo = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
      } else if (diffDays < 7) {
        timeAgo = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
      } else {
        timeAgo = date.toLocaleDateString()
      }

      return {
        id: conv.id || conv.conversation_id,
        title: conv.title || 'Untitled Conversation',
        timeAgo,
        lastMessageAt: conv.lastMessageAt || conv.updated_at,
      }
    })
  },

  /**
   * Saves conversation to localStorage for resuming.
   *
   * @param {string} conversationId - The conversation ID
   * @param {Array} messages - The messages to save
   */
  saveConversationToLocal(conversationId, messages) {
    try {
      const conversationData = {
        conversationId,
        messages: this.formatMessagesForChat(messages),
        savedAt: new Date().toISOString(),
      }

      localStorage.setItem('resumedConversation', JSON.stringify(conversationData))
      localStorage.setItem('resumedConversationId', conversationId)
    } catch (error) {
      console.error('Error saving conversation to localStorage:', error)
    }
  },

  /**
   * Loads resumed conversation from localStorage.
   *
   * @returns {Object|null} Conversation data or null if not found
   */
  loadResumedConversation() {
    try {
      const conversationData = localStorage.getItem('resumedConversation')
      if (conversationData) {
        return JSON.parse(conversationData)
      }
    } catch (error) {
      console.error('Error loading resumed conversation:', error)
    }
    return null
  },

  /**
   * Clears resumed conversation from localStorage.
   */
  clearResumedConversation() {
    try {
      localStorage.removeItem('resumedConversation')
      localStorage.removeItem('resumedConversationId')
    } catch (error) {
      console.error('Error clearing resumed conversation:', error)
    }
  },
}
